import React, { createContext, useContext, useState, useCallback } from 'react';
const UserCacheContext = createContext();

export const useUserCache = () => {
  const context = useContext(UserCacheContext);
  if (!context) {
    throw new Error('useUserCache must be used within UserCacheProvider');
  }
  return context;
};

export const UserCacheProvider = ({ children }) => {
  // Main user cache - Map for O(1) lookups
  const [userCache, setUserCache] = useState(new Map());
  
  // Track what's currently being loaded to prevent duplicate requests
  const [loadingUsers, setLoadingUsers] = useState(new Set());
  
  // Track loaded pages for pagination
  const [loadedPages, setLoadedPages] = useState(new Set());
  
  // Real-time update listeners for components
  const [listeners, setListeners] = useState(new Map());

  // Get users from cache
  const getUsersFromCache = useCallback((userIds) => {
    if (!userIds || userIds.length === 0) return [];
    return userIds.map(id => userCache.get(id)).filter(Boolean);
  }, [userCache]);

  // Get all cached users
  const getAllCachedUsers = useCallback(() => {
    return Array.from(userCache.values());
  }, [userCache]);

  // Check if user exists in cache
  const hasUser = useCallback((userId) => {
    return userCache.has(userId);
  }, [userCache]);

  // Check if users are currently being loaded
  const areUsersLoading = useCallback((userIds) => {
    return userIds.some(id => loadingUsers.has(id));
  }, [loadingUsers]);

  // Get missing user IDs that need to be loaded
  const getMissingUsers = useCallback((userIds) => {
    return userIds.filter(id => !userCache.has(id) && !loadingUsers.has(id));
  }, [userCache, loadingUsers]);

  // Update cache with new user data
  const updateCache = useCallback((users) => {
    if (!users || users.length === 0) return;
    
    setUserCache(prev => {
      const newCache = new Map(prev);
      users.forEach(user => {
        if (user && user.uniqueId) {
          newCache.set(user.uniqueId, user);
        }
      });
      return newCache;
    });

    // Notify all listeners about updates
    listeners.forEach((callback, componentId) => {
      try {
        callback(users);
      } catch (error) {
        // Listener error - silently ignore
      }
    });
  }, [listeners]);

  // Update a single user in cache
  const updateUser = useCallback((user) => {
    if (!user || !user.uniqueId) return;
    
    setUserCache(prev => {
      const newCache = new Map(prev);
      newCache.set(user.uniqueId, user);
      return newCache;
    });

    // Notify listeners about single user update
    listeners.forEach((callback, componentId) => {
      try {
        callback([user]);
      } catch (error) {
        // Listener error - silently ignore
      }
    });
  }, [listeners]);

  // Mark users as loading
  const markUsersLoading = useCallback((userIds) => {
    setLoadingUsers(prev => new Set([...prev, ...userIds]));
  }, []);

  // Mark users as loaded (remove from loading set)
  const markUsersLoaded = useCallback((userIds) => {
    setLoadingUsers(prev => {
      const newSet = new Set(prev);
      userIds.forEach(id => newSet.delete(id));
      return newSet;
    });
  }, []);

  // Track loaded pages for pagination
  const markPageLoaded = useCallback((pageNumber) => {
    setLoadedPages(prev => new Set([...prev, pageNumber]));
  }, []);

  const isPageLoaded = useCallback((pageNumber) => {
    return loadedPages.has(pageNumber);
  }, [loadedPages]);

  // Register listener for real-time updates
  const addListener = useCallback((componentId, callback) => {
    setListeners(prev => new Map([...prev, [componentId, callback]]));
  }, []);

  // Remove listener (cleanup)
  const removeListener = useCallback((componentId) => {
    setListeners(prev => {
      const newListeners = new Map(prev);
      newListeners.delete(componentId);
      return newListeners;
    });
  }, []);

  // Filter cached users by criteria
  const filterUsers = useCallback((filters) => {
    const allUsers = Array.from(userCache.values());
    
    let filtered = allUsers;
    
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(user => 
        user.email?.toLowerCase().includes(searchLower) ||
        user.givenName?.toLowerCase().includes(searchLower) ||
        user.familyName?.toLowerCase().includes(searchLower) ||
        user.uniqueId?.toLowerCase().includes(searchLower)
      );
    }
    
    if (filters.authorizationGrade) {
      filtered = filtered.filter(user => 
        user.authorizationGrade === filters.authorizationGrade
      );
    }
    
    if (filters.planType) {
      filtered = filtered.filter(user => 
        user.planType === filters.planType
      );
    }

    if (filters.hasPaid !== undefined) {
      filtered = filtered.filter(user => 
        user.hasPaid === filters.hasPaid
      );
    }
    
    return filtered;
  }, [userCache]);

  // Clear cache (for logout or refresh)
  const clearCache = useCallback(() => {
    setUserCache(new Map());
    setLoadingUsers(new Set());
    setLoadedPages(new Set());
    setListeners(new Map());
  }, []);

  // Get cache statistics
  const getCacheStats = useCallback(() => {
    return {
      totalUsers: userCache.size,
      loadingUsers: loadingUsers.size,
      loadedPages: loadedPages.size,
      activeListeners: listeners.size
    };
  }, [userCache.size, loadingUsers.size, loadedPages.size, listeners.size]);

  const value = {
    // Data access
    getUsersFromCache,
    getAllCachedUsers,
    hasUser,
    filterUsers,
    
    // Loading state
    areUsersLoading,
    getMissingUsers,
    markUsersLoading,
    markUsersLoaded,
    
    // Pagination
    isPageLoaded,
    markPageLoaded,
    
    // Updates
    updateCache,
    updateUser,
    
    // Listeners for real-time updates
    addListener,
    removeListener,
    
    // Utilities
    clearCache,
    getCacheStats
  };

  return (
    <UserCacheContext.Provider value={value}>
      {children}
    </UserCacheContext.Provider>
  );
};
